'use client';
import { faFacebook, faInstagram, faTiktok, faYoutube } from '@fortawesome/free-brands-svg-icons';
import dayjs from 'dayjs';
import { useParams } from 'next/navigation';
import { Icon, Link } from '@/components/ui';
import { RenderableImage } from '@/components/ui/image/renderable-image';
import { resolveFooterLinkTarget } from '@/configs/footer/footer.config';
import useFooterContent from '@/hooks/use-footer-content';
import { useResolveThemeColor } from '@/hooks/use-resolve-theme-color';
import { useAppContext, useTranslation } from '@/providers/app-provider/app-provider';
import { cn } from '@/utils';

const DefaultFooter = () => {
  const resolveThemeColor = useResolveThemeColor();
  const footerConfig = useFooterContent();
  const { isStudioPreview, studioViewport } = useAppContext<{
    isStudioPreview?: boolean;
    studioViewport?: 'mobile' | 'tablet' | 'desktop';
  }>();
  const { t } = useTranslation();
  const { locale } = useParams();

  const useDesktopLayout =
    isStudioPreview === true ? (studioViewport ?? 'desktop') === 'desktop' : true;

  const iconByKey = {
    youtube: faYoutube,
    instagram: faInstagram,
    facebook: faFacebook,
    tiktok: faTiktok,
  } as const;

  return (
    <footer
      className="bg-secondary text-default-600 border-t-[16px]"
      style={{
        borderTopColor: resolveThemeColor(footerConfig?.topBorderColor) || 'transparent',
        background: resolveThemeColor(footerConfig?.contentBackground),
      }}
    >
      <div
        className={cn(
          'container mx-auto flex flex-col justify-between gap-4 px-4 pt-16',
          isStudioPreview
            ? useDesktopLayout && 'flex-row items-start justify-between gap-16 px-12'
            : 'lg:flex-row lg:items-start lg:justify-between lg:gap-16 lg:px-12',
        )}
      >
        {footerConfig && (
          <>
            <div
              className={cn(
                'mb-10 max-w-sm',
                isStudioPreview
                  ? useDesktopLayout
                    ? 'mb-0 max-w-[220px]'
                    : 'mb-10 max-w-sm'
                  : 'lg:mb-0',
              )}
            >
              {footerConfig.banner.logoSrc ? (
                <RenderableImage
                  src={footerConfig.banner.logoSrc}
                  alt="Company logo"
                  width={80}
                  height={40}
                />
              ) : null}
              <p
                className="mt-4 text-sm leading-relaxed"
                style={{ color: resolveThemeColor(footerConfig.contentTextColor ?? 'default-200') }}
              >
                {footerConfig.banner.description}
              </p>
            </div>
            <div
              className={cn(
                'grid flex-1 grid-cols-2 items-start gap-x-12 gap-y-8 text-sm',
                isStudioPreview ? useDesktopLayout && 'grid-cols-2' : 'lg:grid-cols-2',
              )}
            >
              {footerConfig.sections.map((section, index) => (
                <div key={index}>
                  <p
                    className="mb-4 font-medium"
                    style={{
                      color: resolveThemeColor(footerConfig.contentTitleColor ?? 'foreground'),
                    }}
                  >
                    {section.title}
                  </p>
                  <ul>
                    {section.items.map((item, itemIndex) => {
                      const target = resolveFooterLinkTarget(item);
                      const linkStyle = {
                        color: resolveThemeColor(footerConfig.contentLinkColor ?? 'default-200'),
                      };
                      return (
                        <li key={itemIndex} className="mb-2">
                          {target.mode === 'external' ? (
                            <a
                              href={target.href}
                              className="cursor-pointer text-sm font-light"
                              style={linkStyle}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {item.label}
                            </a>
                          ) : (
                            <Link
                              href={target.href}
                              className="cursor-pointer text-sm font-light"
                              style={linkStyle}
                            >
                              {item.label}
                            </Link>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      {(footerConfig.trustSignals ?? []).length > 0 && (
        <div
          className={cn(
            'container mx-auto flex flex-wrap items-center gap-3 px-4 py-4',
            isStudioPreview ? useDesktopLayout && 'px-12' : 'lg:px-12',
          )}
        >
          {footerConfig.trustSignals!.map(({ image, link, alt }, index) => {
            const img = (
              <RenderableImage
                key={index}
                src={image}
                alt={alt || `Trust signal ${index + 1}`}
                className="h-16 w-auto object-contain"
              />
            );
            if (link) {
              const isExternal = link.startsWith('http');
              const href = isExternal
                ? link
                : `/${locale}${link.startsWith('/') ? '' : '/'}${link}`;
              return (
                <a key={index} href={href} target="_blank" rel="noopener noreferrer">
                  {img}
                </a>
              );
            }
            return img;
          })}
        </div>
      )}
      <div
        className={cn(
          'text-foreground-100 mt-12 px-4 text-sm',
          isStudioPreview ? useDesktopLayout && 'px-12' : 'lg:px-12',
        )}
        style={{
          background: resolveThemeColor(footerConfig?.background) || 'transparent',
        }}
      >
        <div
          className={cn(
            'container mx-auto flex flex-col gap-4 p-4',
            footerConfig.banner.socials.length > 0
              ? 'items-center justify-between'
              : 'items-center justify-center',
            isStudioPreview ? useDesktopLayout && 'flex-row' : 'lg:flex-row',
          )}
        >
          <div
            className={cn(
              'text-center',
              footerConfig.banner.socials.length > 0
                ? isStudioPreview
                  ? useDesktopLayout && 'text-left'
                  : 'lg:text-left'
                : isStudioPreview
                  ? useDesktopLayout && 'text-center'
                  : 'lg:text-center',
            )}
          >
            © {dayjs().year()} <span className="">e3</span> {t?.common.layout.footer}
            {` v${process.env.NEXT_PUBLIC_DEPLOYMENT_VERSION}`}
          </div>

          {footerConfig.banner.socials.length > 0 && (
            <div className="flex items-center gap-4">
              {footerConfig.banner.socials.map((item, index) => (
                <a
                  key={index}
                  href={item.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={item.icon || `Social link ${index + 1}`}
                >
                  {item.icon && item.icon in iconByKey ? (
                    <Icon
                      icon={iconByKey[item.icon as keyof typeof iconByKey]}
                      size="lg"
                      className="hover:opacity-80"
                      style={{
                        color: resolveThemeColor(footerConfig.socialIconColor ?? 'default-200'),
                      }}
                    />
                  ) : item.iconSrc ? (
                    <RenderableImage
                      src={item.iconSrc}
                      alt={item.iconSrc}
                      width={24}
                      height={24}
                      className="rounded-none hover:opacity-80"
                    />
                  ) : null}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </footer>
  );
};

export default DefaultFooter;
